import { useState, useEffect } from 'react';
import {
  BarChart3,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import {
  Heading,
  BodyText,
  Card,
  MetricCard,
  MetricCardGrid,
  METRIC_READ_CAPTIONS,
  Button,
  Skeleton,
  formatUsd,
  formatUsdCompact,
} from '@aaimplatform/ui';
import { apiClient } from '../api/client';
import { readErrorCopy } from '../api/error-message';
import { loanTypeLabel } from '../api/loan-labels';
import type { PlatformAdmin } from '@aaimplatform/institutional-types';
import { formatPercent } from './monitoring/display-format';
import { DashboardSectionLabel } from './dashboards/dashboard-chrome';

type PortfolioSummary = PlatformAdmin.components['schemas']['PortfolioSummary'];

/**
 * How metrics.averageLtv was computed, keyed on the generated enum so a basis
 * added to the spec fails tsc here until it has a caption (ruling
 * ltv-basis-visible).
 */
const AVERAGE_LTV_BASIS_CAPTIONS: Record<
  NonNullable<PortfolioSummary['metrics']['averageLtvBasis']>,
  string
> = {
  OUTSTANDING_OVER_ELIGIBLE_VALUE: 'Outstanding balance over eligible collateral value',
};

/** What a figure prints in place of a value the response did not carry. */
const WITHHELD = '--';

const READ_FAILED_COPY =
  'Portfolio data could not be loaded. Retry, or contact support if it persists.';

// One money and percent rule with the Collateral Monitoring pages
// (monitoring/display-format.ts): a tile prints compact ('$125K'), a row
// figure prints in full ('$80,000.00'), and a percentage prints at one
// decimal place ('80.0%'). The page kept its own whole-dollar and raw
// `${value}%` formatters, so one book read '$125,000' and '80%' here and
// '$125K' and '80.0%' there (UI run r3, fi-g8-7, fi-cons-1, fi-cons-2).

/** A headline figure on a tile. */
const tileMoney = (amount: number | undefined): string =>
  amount == null ? WITHHELD : formatUsdCompact(amount);

/** A figure on a composition row. */
const rowMoney = (amount: number | undefined): string =>
  amount == null ? WITHHELD : formatUsd(amount);

const percent = (value: number | undefined): string =>
  value == null ? WITHHELD : formatPercent(value);

const plural = (count: number, noun: string): string =>
  `${count} ${noun}${count !== 1 ? 's' : ''}`;

/**
 * The LTV bands platform-admin-api buckets loans into
 * (services/platform-admin-api/src/routes/portfolio.routes.ts). They are fixed
 * edges, not the institution's CHA thresholds, so the labels state the band
 * and make no risk or tier claim.
 */
const LTV_BANDS = [
  { key: 'low', label: 'LTV below 50%' },
  { key: 'medium', label: 'LTV 50% to 70%' },
  { key: 'high', label: 'LTV 70% and above' },
] as const;

/**
 * Portfolio Overview
 *
 * Displays aggregated loan portfolio data from the platform-admin-api.
 * All data is real, queried from Loan and CollateralPackage tables.
 *
 * A figure the response omits prints '--' with METRIC_READ_CAPTIONS.notReported,
 * never a measured $0 or 0%. The marginHealth block left the contract (ruling
 * absence-representable-contracts): it summed CollateralPackage columns that
 * default to 0, so a package that was never assessed read as "Healthy".
 * Average LTV is outstanding balance over eligible collateral value across
 * assessed loans, and the tile names that basis and how many loans it leaves
 * out (ruling ltv-basis-visible). The concentration row renders the measured
 * share only; its limit and verdict are not read from the institution's
 * concentration policy, so the page does not present them as a configured limit.
 */
export function PortfolioPage() {
  const [data, setData] = useState<PortfolioSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.get<PortfolioSummary>('/admin/portfolio/summary');
      setData(result);
    } catch (err) {
      setError(readErrorCopy(err, READ_FAILED_COPY));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <MetricCardGrid>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </MetricCardGrid>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card className="p-8 text-center">
        <AlertTriangle className="w-8 h-8 text-warning mx-auto mb-3" />
        <BodyText variant="ui" color="secondary">{error ?? 'No portfolio data was returned.'}</BodyText>
        <Button variant="secondary" onClick={fetchData} className="mt-4">
          Retry
        </Button>
      </Card>
    );
  }

  const { metrics, riskDistribution, loanTypeBreakdown, concentration } = data;
  const activeLoans = metrics.activeLoans;
  // An aggregate over zero loans is not a measurement.
  const hasLoans = activeLoans != null && activeLoans > 0;
  const unassessed =
    metrics.unassessedLoans != null ? `${plural(metrics.unassessedLoans, 'loan')} not assessed` : undefined;
  const ltvBasis = metrics.averageLtvBasis ? AVERAGE_LTV_BASIS_CAPTIONS[metrics.averageLtvBasis] : undefined;
  const averageLtvDescription = !hasLoans
    ? undefined
    : metrics.averageLtv != null
      ? [ltvBasis, unassessed].filter(Boolean).join(', ') || undefined
      : unassessed;
  const alerts = metrics.activeAlerts;

  return (
    <div>
      {/* Page Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          {/* Page title lives in the sticky topnav (AppHeader); the body opens
              with the descriptive subtitle, no redundant H1. */}
          <p className="text-sm font-sans text-tertiary">Aggregated loan portfolio metrics and LTV distribution</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="secondary" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Portfolio Metrics: the kit tile row, two across on a phone. */}
      <MetricCardGrid className="mb-6">
        <MetricCard
          label="Total Collateral Value"
          value={tileMoney(metrics.totalPortfolioValue)}
          caption={metrics.totalPortfolioValue == null ? METRIC_READ_CAPTIONS.notReported : undefined}
          description={activeLoans != null ? `Across ${plural(activeLoans, 'active loan')}` : undefined}
        />
        <MetricCard
          label="Outstanding Balance"
          value={tileMoney(metrics.totalOutstanding)}
          caption={metrics.totalOutstanding == null ? METRIC_READ_CAPTIONS.notReported : undefined}
          description={metrics.activeBorrowers != null ? plural(metrics.activeBorrowers, 'borrower') : undefined}
        />
        <MetricCard
          label="Average LTV"
          value={hasLoans ? percent(metrics.averageLtv) : WITHHELD}
          caption={
            !hasLoans
              ? 'No active loans'
              : metrics.averageLtv == null
                ? METRIC_READ_CAPTIONS.notReported
                : undefined
          }
          description={averageLtvDescription}
        />
        {/* The same label as the Collateral Monitoring tile. That tile counts
            every open alert of the institution; this one counts open alerts
            (the same OPEN_STATUSES) on active loans only, per
            platform-admin-api portfolio.routes.ts, so the description names
            the scope (UI run r3, fi-g8-6). */}
        <MetricCard
          label="Collateral Health Alerts"
          value={alerts == null ? WITHHELD : String(alerts)}
          caption={alerts == null ? METRIC_READ_CAPTIONS.notReported : undefined}
          description={
            alerts == null
              ? undefined
              : alerts > 0
                ? `${plural(alerts, 'unresolved alert')} on active loans`
                : 'No unresolved alerts'
          }
        />
      </MetricCardGrid>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        {/* Loan Type Breakdown */}
        <div className="lg:col-span-2">
          <Card>
            <div className="flex items-center justify-between mb-6">
              <div>
                {/* Card title scale, as every card title in the portal (UI run
                    r3, fi-cons-9); level 3 is sans. Interface copy is BodyText
                    variant ui, not the serif content default (fi-g8-8). */}
                <Heading level={3} size="card">Loan Composition</Heading>
                <BodyText variant="ui" size="sm" color="secondary">By loan type</BodyText>
              </div>
              <BarChart3 className="w-5 h-5 text-tertiary" />
            </div>

            {loanTypeBreakdown.length === 0 ? (
              <BodyText variant="ui" size="sm" color="tertiary" className="text-center py-8">
                No active loans in portfolio
              </BodyText>
            ) : (
              <div className="space-y-4">
                {loanTypeBreakdown.map((item) => (
                  <div
                    key={item.type}
                    data-testid="loan-composition-row"
                    className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4"
                  >
                    <div className="sm:w-32 text-sm font-sans font-medium text-primary leading-normal">
                      {loanTypeLabel(item.type)}
                    </div>
                    <div className="w-full sm:flex-1">
                      <div className="h-6 bg-elevated rounded-sm overflow-hidden">
                        <div
                          data-testid="loan-composition-bar"
                          className="h-full bg-neutral-data rounded-sm transition-all duration-500"
                          style={{ width: `${item.percentage ?? 0}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex items-baseline justify-between gap-4 sm:contents">
                      <div className="sm:w-20 sm:text-right">
                        <div className="text-sm font-mono font-semibold text-primary tabular-nums leading-normal">
                          {percent(item.percentage)}
                        </div>
                      </div>
                      <div className="text-right sm:w-32">
                        <div className="text-sm font-mono text-primary tabular-nums leading-normal">
                          {rowMoney(item.value)}
                        </div>
                        {item.count != null && (
                          <div className="text-xs font-sans text-tertiary leading-normal">
                            {plural(item.count, 'loan')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* LTV Distribution */}
        <Card>
          <div className="mb-6">
            <Heading level={3} size="card">LTV Distribution</Heading>
            <BodyText variant="ui" size="sm" color="secondary">Share of active loans by LTV band</BodyText>
            <BodyText variant="ui" size="xs" color="tertiary" className="mt-1">
              Fixed bands, not your CHA thresholds
            </BodyText>
          </div>

          {hasLoans ? (
            <div className="space-y-6">
              {LTV_BANDS.map((band) => {
                const value = riskDistribution[band.key];
                return (
                  <div key={band.key}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-sans font-medium text-primary leading-normal">
                        {band.label}
                      </span>
                      <span className="text-sm font-mono font-semibold text-primary tabular-nums leading-normal">
                        {percent(value)}
                      </span>
                    </div>
                    <div className="h-3 bg-elevated rounded-sm overflow-hidden">
                      <div
                        className="h-full bg-neutral-data rounded-sm transition-all duration-500"
                        style={{ width: `${value ?? 0}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <BodyText variant="ui" size="sm" color="tertiary" className="text-center py-8">
              No active loans in portfolio
            </BodyText>
          )}
        </Card>
      </div>

      {/* Borrower Concentration: the measured share only. A section of kit
          tiles, like the tile row above it; the share sat in a hand-bordered
          box inside a Card (UI run r3, fi-g8-10). */}
      {hasLoans && concentration.length > 0 && (
        <section>
          <DashboardSectionLabel>Borrower Concentration</DashboardSectionLabel>
          <MetricCardGrid columns={3}>
            {concentration.map((row, i) => (
              <MetricCard
                key={row.type ?? i}
                label={row.type ?? 'Concentration'}
                value={percent(row.current)}
                caption={row.current == null ? METRIC_READ_CAPTIONS.notReported : undefined}
                description="of outstanding balance"
              />
            ))}
          </MetricCardGrid>
          <BodyText variant="ui" size="xs" color="secondary" className="mt-3">
            {concentration.length === 1
              ? 'No limit from your concentration policy is applied to this figure.'
              : 'No limit from your concentration policy is applied to these figures.'}
          </BodyText>
        </section>
      )}
    </div>
  );
}
